import { useEffect, useState } from 'react';

// material-ui
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// project import
import NavGroup from './NavGroup';
import * as icons from '@ant-design/icons';
import getApiUrl from 'utils/getApiUrl';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

// Fungsi rekursif untuk memetakan nama icon (string) ke komponen icon dari @ant-design/icons
const mapIcons = (menuArray) => {
  return menuArray.map((item) => {
    const mappedItem = { ...item };
    if (mappedItem.icon && typeof mappedItem.icon === 'string' && icons[mappedItem.icon]) {
      mappedItem.icon = icons[mappedItem.icon];
    } else {
      // Smart Fallback jika icon kosong atau tidak ditemukan di Ant Design Icons
      const titleLower = (mappedItem.title || '').toLowerCase();
      if (titleLower.includes('laporan')) {
        mappedItem.icon = icons['FileTextOutlined'];
      } else if (titleLower.includes('daftar') || titleLower.includes('list')) {
        mappedItem.icon = icons['UnorderedListOutlined'];
      } else if (titleLower.includes('input') || titleLower.includes('form')) {
        mappedItem.icon = icons['FormOutlined'];
      } else {
        mappedItem.icon = icons['FileTextOutlined'] || icons['QuestionOutlined'];
      }
    }
    if (mappedItem.children) {
      mappedItem.children = mapIcons(mappedItem.children);
    }
    return mappedItem;
  });
};

export default function Navigation() {
  const [menuItems, setMenuItems] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const authUser = localStorage.getItem('auth_user');
        const user = authUser ? JSON.parse(authUser) : null;
        const token = (user && user.token) || localStorage.getItem('token') || '';

        const headers = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/menu`, { headers });
        const data = await response.json();

        if (data.menu) {
          const processedMenu = mapIcons(data.menu);

          // Reorder: move Master data below Gudang & Inventori
          const masterIndex = processedMenu.findIndex((g) => g.title && g.title.toLowerCase() === 'master data');
          if (masterIndex !== -1) {
            const masterGroup = processedMenu.splice(masterIndex, 1)[0];
            const gudangIndex = processedMenu.findIndex((g) => g.title && g.title.toLowerCase() === 'gudang & inventori');
            if (gudangIndex !== -1) {
              processedMenu.splice(gudangIndex + 1, 0, masterGroup);
            } else {
              processedMenu.push(masterGroup);
            }
          }

          // Move Produksi group directly above Master Data and inject Laporan Finish Good
          const prodIndex = processedMenu.findIndex((g) => g.title && g.title.toLowerCase() === 'produksi');
          if (prodIndex !== -1) {
            const prodGroup = processedMenu.splice(prodIndex, 1)[0];
            if (!prodGroup.children) {
              prodGroup.children = [];
            }
            const hasLaporanFG = prodGroup.children.some(
              (c) => (c.url && c.url.includes('laporan-finish-good')) || (c.title && c.title.toLowerCase().includes('laporan finish good'))
            );
            if (!hasLaporanFG) {
              prodGroup.children.push({
                id: 'laporan-finish-good',
                title: 'Laporan Finish Good',
                type: 'item',
                url: '/produksi/laporan-finish-good',
                icon: icons['FileTextOutlined'] || icons['CheckCircleOutlined']
              });
            }

            const targetMasterIdx = processedMenu.findIndex((g) => g.title && g.title.toLowerCase() === 'master data');
            if (targetMasterIdx !== -1) {
              processedMenu.splice(targetMasterIdx, 0, prodGroup);
            } else {
              processedMenu.push(prodGroup);
            }
          }

          // Inject Stok Opname into Gudang & Inventori
          const gudangGroupIndex = processedMenu.findIndex((g) => g.title && g.title.toLowerCase() === 'gudang & inventori');
          if (gudangGroupIndex !== -1) {
            const stokOpnameMenu = {
              id: 'stok-opname-collapse',
              title: 'Stok Opname',
              type: 'collapse',
              icon: icons['ProfileOutlined'] || icons['QuestionOutlined'],
              children: [
                { id: 'cetak-daftar-barang', title: 'Cetak Daftar Barang', type: 'item', url: '/gudang/cetak-daftar-barang' },
                { id: 'opname', title: 'Stok Opname', type: 'item', url: '/gudang/opname' },
                { id: 'persetujuan-opname', title: 'Persetujuan Opname', type: 'item', url: '/gudang/persetujuan-opname', level: '1' },
                { id: 'cetak-opname', title: 'Cetak Stok Opname', type: 'item', url: '/gudang/cetak-opname' },
                { id: 'laporan-opname', title: 'Laporan Stok Opname', type: 'item', url: '/gudang/laporan-opname' },
                { id: 'laporan-audit-stok', title: 'Laporan Audit Selisih Stok', type: 'item', url: '/gudang/laporan-audit', level: '0' }
              ]
            };
            if (!processedMenu[gudangGroupIndex].children) {
              processedMenu[gudangGroupIndex].children = [];
            }
            // Hapus menu 'Stok Opname' bawaan dari database agar tidak double
            processedMenu[gudangGroupIndex].children = processedMenu[gudangGroupIndex].children.filter(
              (c) => c.title?.toLowerCase() !== 'stok opname'
            );

            // Tambahkan menu Stok Opname custom (tipe collapse)
            processedMenu[gudangGroupIndex].children.push(stokOpnameMenu);
          }

          const authUser = localStorage.getItem('auth_user');
          const user = authUser ? JSON.parse(authUser) : null;

          // Filter menu berdasarkan kolom 'level'
          const filterMenuByLevel = (menuList, currentUser) => {
            return menuList.filter((item) => {
              // Jika menu punya level, cek hak akses
              if (item.level) {
                if (!currentUser) return false;

                const userLevel =
                  currentUser.level !== undefined && currentUser.level !== null
                    ? String(currentUser.level)
                    : currentUser.id_level !== undefined && currentUser.id_level !== null
                      ? String(currentUser.id_level)
                      : currentUser.level_user !== undefined && currentUser.level_user !== null
                        ? String(currentUser.level_user)
                        : '';

                if (currentUser.role === 'admin' || userLevel === '0') return true;

                const allowedLevels = item.level.split(',').map((l) => l.trim());
                if (!allowedLevels.includes(userLevel)) {
                  return false;
                }
              }

              // Filter children secara rekursif
              if (item.children && item.children.length > 0) {
                item.children = filterMenuByLevel(item.children, currentUser);
                // Jika setelah difilter children-nya kosong dan tipenya grup/collapse, sembunyikan juga
                if (item.children.length === 0 && (item.type === 'group' || item.type === 'collapse')) {
                  return false;
                }
              }
              return true;
            });
          };

          let filteredMenu = filterMenuByLevel(processedMenu, user);

          // Khusus untuk User Level 0 & ID Gudang 0: Hanya tampilkan group Dashboard dan Group Laporan
          const isSuperAdminLevel0 = user &&
            (String(user.level) === '0' || user.role === 'superadmin') &&
            (user.id_gudang === undefined || user.id_gudang === null || Number(user.id_gudang) === 0);

          if (isSuperAdminLevel0) {
            // 1. Ambil / Buat Group Dashboard
            const dashboardGroup = processedMenu.find(
              (g) => (g.title || '').toLowerCase().includes('navigation') || (g.title || '').toLowerCase().includes('dashboard') || g.id === 'group-dashboard'
            ) || {
              id: 'group-dashboard',
              title: 'Navigation',
              type: 'group',
              children: [
                {
                  id: 'dashboard',
                  title: 'Dashboard',
                  type: 'item',
                  url: '/dashboard/default',
                  icon: icons['DashboardOutlined'],
                  breadcrumbs: false
                }
              ]
            };

            // 2. Kumpulkan seluruh menu laporan (deduplikasi berdasarkan title)
            const uniqueLaporanMap = new Map();
            const normalizeKey = (str) => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');

            const addLaporanItem = (item) => {
              const key = normalizeKey(item.title);
              if (!key) return;
              if (!uniqueLaporanMap.has(key)) {
                uniqueLaporanMap.set(key, item);
              }
            };

            const extractLaporanItems = (items) => {
              if (!items) return;
              items.forEach((item) => {
                const titleLower = (item.title || '').toLowerCase();
                const urlLower = (item.url || '').toLowerCase();
                const isLaporan =
                  titleLower.includes('laporan') ||
                  titleLower.includes('mutasi') ||
                  titleLower.includes('cetak') ||
                  urlLower.includes('laporan') ||
                  urlLower.includes('mutasi') ||
                  urlLower.includes('cetak');

                if (item.type === 'item' && isLaporan) {
                  addLaporanItem(item);
                }
                if (item.children) {
                  extractLaporanItems(item.children);
                }
              });
            };

            extractLaporanItems(processedMenu);

            // Standar Laporan items fallback jika belum ada di database response
            const standardLaporanItems = [
              { id: 'laporan-mutasi-stok', title: 'Laporan Mutasi Stok', type: 'item', url: '/gudang/laporan-mutasi', icon: 'FileTextOutlined' },
              { id: 'laporan-barang-masuk', title: 'Laporan Barang Masuk', type: 'item', url: '/gudang/laporan-barang-masuk', icon: 'FileTextOutlined' },
              { id: 'laporan-barang-keluar', title: 'Laporan Barang Keluar', type: 'item', url: '/gudang/laporan-barang-keluar', icon: 'FileTextOutlined' },
              { id: 'laporan-opname', title: 'Laporan Stok Opname', type: 'item', url: '/gudang/laporan-opname', icon: 'FileTextOutlined' },
              { id: 'cetak-daftar-barang', title: 'Cetak Daftar Barang', type: 'item', url: '/gudang/cetak-daftar-barang', icon: 'PrinterOutlined' },
              { id: 'cetak-opname', title: 'Cetak Stok Opname', type: 'item', url: '/gudang/cetak-opname', icon: 'PrinterOutlined' },
              { id: 'laporan-audit-stok', title: 'Laporan Audit Selisih Stok', type: 'item', url: '/gudang/laporan-audit', icon: 'SafetyCertificateOutlined', level: '0' },
              { id: 'laporan-finish-good', title: 'Laporan Finish Good', type: 'item', url: '/produksi/laporan-finish-good', icon: 'FileTextOutlined' },
              { id: 'tarik-pembayaran', title: 'Laporan Tarik Pembayaran', type: 'item', url: '/laporan/tarik-pembayaran', icon: 'FileTextOutlined' }
            ];

            standardLaporanItems.forEach((stdItem) => {
              addLaporanItem(stdItem);
            });

            const laporanChildren = Array.from(uniqueLaporanMap.values());

            const laporanGroup = {
              id: 'group-laporan',
              title: 'Laporan',
              type: 'group',
              children: mapIcons(laporanChildren)
            };

            filteredMenu = [dashboardGroup, laporanGroup];
          }

          setMenuItems({ items: filteredMenu });
        }
      } catch (error) {
        console.error('Failed to fetch menu:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const navGroups = menuItems.items.map((item) => {
    switch (item.type) {
      case 'group':
        return <NavGroup key={item.id} item={item} />;
      default:
        return (
          <Typography key={item.id} variant="h6" sx={{ color: 'error.main', textAlign: 'center' }}>
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return <Box sx={{ pt: 2 }}>{navGroups}</Box>;
}
